import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

export default function SidebarCollapsible({
    isOpen,
    children,
    className,
    id,
}) {
    const contentRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(0);

    const measureHeight = useCallback(() => {
        if (!contentRef.current) {
            return;
        }
        setContentHeight(contentRef.current.scrollHeight);
    }, []);

    useLayoutEffect(() => {
        measureHeight();
    }, [measureHeight, children]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        measureHeight();
    }, [isOpen, measureHeight]);

    useEffect(() => {
        const node = contentRef.current;
        if (!node || typeof window === "undefined" || typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            measureHeight();
        });

        observer.observe(node);
        return () => observer.disconnect();
    }, [measureHeight]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }
        window.addEventListener("resize", measureHeight);
        return () => window.removeEventListener("resize", measureHeight);
    }, [measureHeight]);

    const containerClassName = `band-sidebar__collapsible${isOpen ? " band-sidebar__collapsible--open" : ""}${className ? ` ${className}` : ""}`;

    return (
        <div
            id={id}
            className={containerClassName}
            style={{ maxHeight: isOpen ? `${contentHeight}px` : 0 }}
            aria-hidden={!isOpen}
        >
            <div ref={contentRef} className="band-sidebar__collapsible-inner">
                {children}
            </div>
        </div>
    );
}

SidebarCollapsible.propTypes = {
    isOpen: PropTypes.bool,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    id: PropTypes.string,
};
